import { Store } from "../utils/Store";
import GdprStorage from "../storage/GdprStorage";
import { PrefixedStorage } from "../PrefixedStorage";
import { JSONHandlingStorage } from "../JSONHandlingStorage";
import CountryUtil from "../utils/CountryUtil";
import LocaleUtil from "../utils/LocaleUtil";
import { ApiUrlUtil } from "../utils/ApiUrlUtil";
import type { JsonStorageFrontend } from "../JSONHandlingStorage";

const INITIAL = "initial";
const PENDING = "pending";
const LOGGEDIN = "loggedin";
const NOTLOGGEDIN = "notLoggedin";
const LOGOUT = "logout";
const TIMEDOUT = "timedOut";
const FAILED = "failed";
export type LoginStateType =
	| typeof INITIAL
	| typeof PENDING
	| typeof LOGGEDIN
	| typeof NOTLOGGEDIN
	| typeof LOGOUT
	| typeof TIMEDOUT
	| typeof FAILED;
const SELECTED_CUSTOMER = "UI_DISPATCHER_SELECTED_CUSTOMER_ID";
const SELECTED_CARDHOLDER = "UI_DISPATCHER_SELECTED_CARDHOLDER_NUMBER";
const CUSTOMER_ADDRESS_PREFIX = "customerAdr_";
const CUSTOMER_ADDRESS_HASH_PREFIX = "adrHash_";
const MOST_RECENTLY_SELECTED_ADDRESS = CUSTOMER_ADDRESS_PREFIX + "most_recently_selected";
type SessionStateStoreState = {
	salesLine: string | null | undefined;
	locale: string | null | undefined;
	anonymousUserId: string | null | undefined;
	betty2SitecoreStoreIdMapping: Record<string, any> | undefined;
	sitecore2BettyStoreIdMapping: Record<string, any> | undefined;
	onlineVisibleStores: Set<string> | undefined;
	customerId: string | null | undefined;
	customerStoreId: string | null | undefined;
	customerTierLevel: string | null | undefined;
	cardholderNumber: string | null | undefined;
	employeeData: Record<string, any> | null | undefined;
	authorizationData: Record<string, any> | undefined;
	selectedCustomerStore: JsonStorageFrontend | undefined;
	domainNameWithoutSubdomains: string | null | undefined;
	deliveryAddress: Record<string, any> | null | undefined;
	loginState: LoginStateType;
};

class SessionState extends Store {
	_salesLine: string;
	_locale: string;
	_anonymousUserId: string;
	_betty2SitecoreStoreIdMapping: Record<string, string>;
	_sitecore2BettyStoreIdMapping: Record<string, string>;
	_onlineVisibleStores: Set<string>;
	_customerId: string | null | undefined;
	_customerStoreId: string | null | undefined;
	_cardholderNumber: string | null | undefined;
	_employeeData: Record<string, any> | null | undefined;
	_authorizationData: Record<string, any>;
	_selectedCustomerStore: JsonStorageFrontend;
	_customerTierLevel: string | null | undefined;
	_domainNameWithoutSubdomains: string;
	_storeSelectedAddress: JsonStorageFrontend;
	_deliveryAddress: Record<string, any> | null | undefined;
	_loginState: LoginStateType;

	constructor() {
		super();
		this.setEmptyState();
	}

	setEmptyState() {
		this._salesLine = CountryUtil.getSalesLine();
		this._locale = LocaleUtil.getLocale();
		// exploreStoreIdMapping and exploreOnlineVisibleStores are set by loading
		// `/explore.content.v1/sitecore/storeIdMappingWithOnlineVisibility` as a script
		this._betty2SitecoreStoreIdMapping = Object.fromEntries(
			Object.entries(window.exploreStoreIdMapping ?? {}).map(([key, value]) => [
				key,
				(value as string).toLowerCase(),
			]),
		);
		this._sitecore2BettyStoreIdMapping = Object.fromEntries(
			Object.entries(this._betty2SitecoreStoreIdMapping).map(([key, value]) => [value, key]),
		);
		this._onlineVisibleStores = new Set(window.exploreOnlineVisibleStores);
		this._customerId = null;
		this._customerStoreId = null;
		this._cardholderNumber = null;
		this._employeeData = null;
		this._authorizationData = {};
		this._selectedCustomerStore = JSONHandlingStorage(GdprStorage.sessionStorage);
		this._domainNameWithoutSubdomains = ApiUrlUtil.getDomainNameWithoutSubdomains(ApiUrlUtil.getDomain());
		const ps = PrefixedStorage("SES2_", GdprStorage.cookie);
		this._storeSelectedAddress = JSONHandlingStorage(ps);
		this._deliveryAddress = null;
		this._loginState = INITIAL;
	}

	getState(): SessionStateStoreState {
		return {
			salesLine: this._salesLine,
			locale: this._locale,
			anonymousUserId: this._anonymousUserId,
			betty2SitecoreStoreIdMapping: this._betty2SitecoreStoreIdMapping,
			sitecore2BettyStoreIdMapping: this._sitecore2BettyStoreIdMapping,
			onlineVisibleStores: this._onlineVisibleStores,
			customerId: this._customerId,
			customerTierLevel: this._customerTierLevel,
			customerStoreId: this._customerStoreId,
			cardholderNumber: this._cardholderNumber,
			employeeData: this._employeeData,
			authorizationData: this._authorizationData,
			selectedCustomerStore: this._selectedCustomerStore,
			domainNameWithoutSubdomains: this._domainNameWithoutSubdomains,
			deliveryAddress: this._deliveryAddress,
			loginState: this._loginState,
		};
	}

	setAnonymousUserId(anonId: string) {
		this._anonymousUserId = anonId;
	}

	setDeliveryAddress(address: Record<string, any> | null | undefined) {
		this._deliveryAddress = address;
	}

	setCustomerStoreId(storeId: string | null | undefined) {
		this._customerStoreId = storeId;
	}

	setCustomerId(customerId: string | null | undefined) {
		this._customerId = customerId;
	}

	setCustomerTierLevel(tierLevel: string | null | undefined) {
		this._customerTierLevel = tierLevel;
	}

	setCardholderNumber(cardholderNumber: string | null | undefined) {
		this._cardholderNumber = cardholderNumber;
	}

	setEmployeeData(employeeData: Record<string, any> | null | undefined) {
		this._employeeData = employeeData;
	}

	setAuthorizationData(authorizationData: Record<string, any>) {
		this._authorizationData = authorizationData;
	}

	setLoginState(loginState: LoginStateType) {
		this._loginState = loginState;
	}

	getSelectedCustomerId(): string | null | undefined {
		return this._selectedCustomerStore?.read(SELECTED_CUSTOMER);
	}

	getSelectedCardholderNumber(): string | null | undefined {
		return this._selectedCustomerStore?.read(SELECTED_CARDHOLDER);
	}

	setSelectedCustomerId(newCustomerId: string | null | undefined, newCardholderNumber: string | null | undefined) {
		this._customerId = newCustomerId;
		this._cardholderNumber = newCardholderNumber;

		this._selectedCustomerStore?.put(SELECTED_CUSTOMER, newCustomerId, GdprStorage.NECESSARY);

		this._selectedCustomerStore?.put(SELECTED_CARDHOLDER, newCardholderNumber, GdprStorage.NECESSARY);
	}

	_getCustomerAddressCookieName(customerId: (string | null | undefined) | null): string {
		return `${CUSTOMER_ADDRESS_PREFIX}${customerId ?? ""}`;
	}

	_getAddressHashCookieName(customerId: (string | null | undefined) | null): string {
		return `${CUSTOMER_ADDRESS_HASH_PREFIX}${customerId ?? ""}`;
	}

	_getSelectedAddress(key: string): any {
		const addressByKey = this._storeSelectedAddress?.read(key);

		if (addressByKey) {
			return addressByKey;
		}

		const addressCandidate = this._storeSelectedAddress?.read(MOST_RECENTLY_SELECTED_ADDRESS) || {};
		const fallbackAddress = addressCandidate.key === key ? addressCandidate : null;

		if (fallbackAddress) {
			delete fallbackAddress.key;
		}

		return fallbackAddress;
	}

	getSelectedAddress(customerId: (string | null | undefined) | null): any {
		return this._getSelectedAddress(this._getCustomerAddressCookieName(customerId));
	}

	getSelectedAddressHash(customerId: (string | null | undefined) | null): any {
		return this._getSelectedAddress(this._getAddressHashCookieName(customerId));
	}

	_setSelectedAddress(key: string, address: Record<string, any>): boolean | undefined {
		const datePlus30Mins = new Date();
		datePlus30Mins.setMinutes(datePlus30Mins.getMinutes() + 30);
		const datePlus7Days = new Date();
		datePlus7Days.setHours(datePlus7Days.getHours() + 7 * 24);

		if (this.isEmployee() && key !== CUSTOMER_ADDRESS_PREFIX) {
			this._storeSelectedAddress?.put(
				MOST_RECENTLY_SELECTED_ADDRESS,
				Object.assign({}, address, {
					key: key,
				}),
				GdprStorage.NECESSARY,
				{
					domain: this.getCookieDomain(),
					secure: true,
					expires: datePlus7Days,
				},
			);
		}

		return this._storeSelectedAddress?.put(key, address, GdprStorage.NECESSARY, {
			domain: this.getCookieDomain(),
			secure: true,
			expires: this.isEmployee() ? datePlus30Mins : datePlus7Days,
		});
	}

	setSelectedAddress(customerId: (string | null | undefined) | null, address: Record<string, any>) {
		return this._setSelectedAddress(this._getCustomerAddressCookieName(customerId), address);
	}

	_removeSelectedAddress(key: string): boolean | undefined {
		const addressCandidate = this._storeSelectedAddress?.read(MOST_RECENTLY_SELECTED_ADDRESS) || {};

		if (addressCandidate.key === key) {
			this._storeSelectedAddress?.remove(MOST_RECENTLY_SELECTED_ADDRESS);
		}

		return this._storeSelectedAddress?.remove(key);
	}

	removeSelectedAddress(customerId: (string | null | undefined) | null): boolean | undefined {
		return this._removeSelectedAddress(this._getCustomerAddressCookieName(customerId));
	}

	loggedIn(): boolean {
		return this._loginState === LOGGEDIN;
	}

	pending(): boolean {
		return this._loginState === PENDING || this._loginState === INITIAL;
	}

	getCustomerStore(): any {
		if (!this.loggedIn() && !CountryUtil.isMarioCountry()) {
			return null;
		}

		const address = this.getSelectedAddress(this._customerId);
		const selectedStoreId = address ? address.storeId : this._customerStoreId;

		if (!this.loggedIn() && CountryUtil.isMarioCountry() && !selectedStoreId) {
			return CountryUtil.getCountryConfig().defaultStore;
		}

		return selectedStoreId;
	}

	getCookieDomain(): string {
		if (this._domainNameWithoutSubdomains === "localhost") {
			return this._domainNameWithoutSubdomains;
		} else {
			return "." + this._domainNameWithoutSubdomains;
		}
	}

	isEmployee(): any {
		return this._employeeData && this._employeeData.upn;
	}
}

export default new SessionState() as SessionState;
